/**
 * JobCalendarPageStandalone - Minimal FullCalendar implementation for testing
 * 
 * This is a clean, minimal version to test if FullCalendar works without other components
 * Use this to isolate bugs and compare with production site
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import thLocale from "@fullcalendar/core/locales/th";
import { Icon } from "@iconify/react";
import moment from "moment";
import "moment/locale/th";
import api from "../../../utils/axios";
import { getJobStatusDisplayShort } from "../../../utils/jobStatusDisplay";
import Swal from "sweetalert2";
import Pagination from "../../components/Pagination";

// FullCalendar CSS - Use the same imports as CalendarWrapper
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";

moment.locale("th");

/** สีแยกตามสถานะ (5 สถานะหลัก) - ใช้ทั้ง legend และ event */
const JOB_STATUS_COLORS = {
  "รอ assign": "rgba(156, 163, 175, 0.85)",      // gray - งานที่ยังไม่ได้ assign
  "ลงงาน": "rgba(100, 116, 139, 0.85)",           // slate - 1 assign
  "Check in ทำงาน": "rgba(59, 130, 246, 0.85)",  // blue - 2 working
  "working": "rgba(59, 130, 246, 0.85)",
  "On-Site": "rgba(59, 130, 246, 0.85)",
  "ปิดงาน รอเซ็น": "rgba(245, 158, 11, 0.85)",  // amber - 3 sign/check out
  "ปิดงาน เซ็นแล้ว": "rgba(245, 158, 11, 0.85)", // amber - เซ็นแล้ว
  "ปิดงาน": "rgba(245, 158, 11, 0.85)",
  "รอส่งงาน": "rgba(245, 158, 11, 0.85)",        // amber - 3 sign/check out
  "ส่งงาน": "rgba(147, 51, 234, 0.85)",          // purple - 4 job close
  "ส่งงาน (Checkout)": "rgba(147, 51, 234, 0.85)",
  "สำเร็จ": "rgba(34, 197, 94, 0.85)",           // green - 5 success
  "ยกเลิก": "rgba(239, 68, 68, 0.85)",
  "เกิดเวลา": "rgba(249, 115, 22, 0.85)",
};

/** สี fallback = 1 assign (ให้จุดในปฏิทินตรงกับ legend) */
const ASSIGNED_EVENT_COLOR = JOB_STATUS_COLORS["ลงงาน"];

const isJobCalendarActionAllowed = (jobStatus) => (jobStatus || "").trim() === "ลงงาน";

/** Multi-select dropdown สำหรับเลือกทีม - แยกไว้ข้างนอกเพื่อให้ dropdown คงเปิดอยู่ตอนติ๊กหลายทีม */
function TeamMultiSelect({ teams, selectedTeams, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchQueryTeam, setSearchQueryTeam] = useState("");
  const selectRef = useRef(null);

  const filteredTeams = useMemo(() => {
    if (!searchQueryTeam.trim()) return teams;
    const q = searchQueryTeam.toLowerCase();
    return teams.filter((t) => t.name.toLowerCase().includes(q));
  }, [teams, searchQueryTeam]);

  const selectedTeamNames = useMemo(() => {
    const isAllSelected = selectedTeams.length === teams.length && teams.length > 0;
    if (isAllSelected) {
      return "ทั้งหมด";
    }
    return teams
      .filter((t) => selectedTeams.includes(t.id))
      .map((t) => t.name)
      .join(", ");
  }, [teams, selectedTeams]);

  const isAllSelected = useMemo(() => {
    return selectedTeams.length === teams.length && teams.length > 0;
  }, [selectedTeams, teams]);

  const toggleTeam = useCallback((teamId) => {
    if (teamId === null) {
      onChange(teams.map((t) => t.id));
      return;
    }
    if (selectedTeams.includes(teamId)) {
      if (selectedTeams.length > 1) {
        onChange(selectedTeams.filter((id) => id !== teamId));
      }
    } else {
      onChange([...selectedTeams, teamId]);
    }
  }, [teams, selectedTeams, onChange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
        setSearchQueryTeam("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={selectRef} className="relative inline-block min-w-[240px]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="border rounded-md px-3 py-1 w-full min-w-[240px] text-left bg-white hover:bg-gray-50 flex items-center justify-between"
        style={{ fontSize: "14px" }}
      >
        <span className="truncate min-w-0 flex-1" style={{ fontSize: "14px" }}>
          {selectedTeamNames || "เลือกทีม..."}
        </span>
        <span className="ml-2 text-gray-400 shrink-0" style={{ fontSize: "14px" }}>
          ▾
        </span>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-50 w-full min-w-0">
          <div className="p-2 border-b">
            <input
              type="text"
              value={searchQueryTeam}
              onChange={(e) => setSearchQueryTeam(e.target.value)}
              placeholder="ค้นหาทีม..."
              className="w-full border rounded px-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
              style={{ fontSize: "14px", height: "36px", paddingTop: "8px", paddingBottom: "8px" }}
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={() => toggleTeam(null)}
                className="cursor-pointer"
                style={{ width: "16px", height: "16px", minWidth: "16px" }}
              />
              <span className="font-medium whitespace-nowrap" style={{ fontSize: "14px" }}>
                ทั้งหมด
              </span>
            </label>

            {filteredTeams.length === 0 ? (
              <div className="px-3 py-2 text-gray-500" style={{ fontSize: "14px" }}>
                ไม่พบทีม
              </div>
            ) : (
              filteredTeams.map((team) => {
                const isSelected = selectedTeams.includes(team.id);
                return (
                  <label
                    key={team.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                      if (e.target.type !== "checkbox") {
                        e.preventDefault();
                        onChange([team.id]);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleTeam(team.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer"
                      style={{ width: "16px", height: "16px", minWidth: "16px" }}
                    />
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "3px",
                        backgroundColor: team.color || "#cccccc",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      className="whitespace-nowrap shrink-0 overflow-visible"
                      style={{ fontSize: "14px" }}
                    >
                      {team.name}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** คืนค่าสีของจุดตาม Job_Status จาก database */
function getEventColorByStatus(jobStatus) {
  const s = (jobStatus || "").trim();
  return JOB_STATUS_COLORS[s] || ASSIGNED_EVENT_COLOR;
}

export default function JobCalendarPageStandalone({ branchFilter = "all" }) {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  
  // State
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]); // Filtered events for display
  const [allEvents, setAllEvents] = useState([]); // All events from API
  const [rightJobs, setRightJobs] = useState([]); // Pending jobs for sidebar
  const [currentJobsList, setCurrentJobsList] = useState([]); // Current jobs for sidebar
  const [loading, setLoading] = useState(true);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]); // Teams selected for daily view
  const [selectedTeamsMonth, setSelectedTeamsMonth] = useState([]); // Teams selected for month view
  const [searchQuery, setSearchQuery] = useState(""); // Search query for filtering
  const [activeTab, setActiveTab] = useState("current"); // "pending" or "current"
  const [showJobInfoModal, setShowJobInfoModal] = useState(false); // Modal แสดงรายละเอียด
  const [selectedJobInfo, setSelectedJobInfo] = useState(null); // ข้อมูลงานที่เลือก
  const [selectedJobCalendarId, setSelectedJobCalendarId] = useState(null); // Calendar_Id ของงานที่เลือก (สำหรับยกเลิก)
  const [jobInfoLoading, setJobInfoLoading] = useState(false); // Loading state สำหรับ modal
  const isHoldingExternalJobRef = useRef(false);
  const frozenScrollTargetsRef = useRef([]);
  const freezeScrollRafRef = useRef(null);
  const [jobListPage, setJobListPage] = useState(1);
  const [jobListItemsPerPage, setJobListItemsPerPage] = useState(10);
  const toolbarOverlayTop = 22;
  const toolbarLegendLeft = 178;
  const toolbarTeamRight = 110;
  const [calendarHeight, setCalendarHeight] = useState(
    Math.max(560, (typeof window !== "undefined" ? window.innerHeight : 900) - 170)
  );

  const isSameRoundJob = (a, b) => {
    const aRoundId = a?.Round_Id;
    const bRoundId = b?.Round_Id;
    if (aRoundId != null && bRoundId != null) {
      return String(aRoundId) === String(bRoundId);
    }
    return (
      String(a?.JobId ?? a?.Job_Id ?? "") === String(b?.JobId ?? b?.Job_Id ?? "") &&
      String(a?.Round_No ?? "") === String(b?.Round_No ?? "")
    );
  };

  useEffect(() => {
    setJobListPage(1);
  }, [activeTab, searchQuery]);

  useEffect(() => {
    const updateCalendarHeight = () => {
      setCalendarHeight(Math.max(560, window.innerHeight - 170));
    };
    updateCalendarHeight();
    window.addEventListener("resize", updateCalendarHeight);
    return () => window.removeEventListener("resize", updateCalendarHeight);
  }, []);

  // ล็อกไม่ให้ปฏิทิน auto-scroll ระหว่าง "กำลังถือ" card งานจาก sidebar
  useEffect(() => {
    const stopFreezeLoop = () => {
      if (freezeScrollRafRef.current != null) {
        cancelAnimationFrame(freezeScrollRafRef.current);
        freezeScrollRafRef.current = null;
      }
    };

    const startFreezeLoop = () => {
      stopFreezeLoop();
      const tick = () => {
        frozenScrollTargetsRef.current.forEach((item) => {
          const el = item?.el;
          if (!(el instanceof HTMLElement)) return;
          if (el.scrollTop !== item.top) el.scrollTop = item.top;
          if (el.scrollLeft !== item.left) el.scrollLeft = item.left;
        });
        if (isHoldingExternalJobRef.current) {
          freezeScrollRafRef.current = requestAnimationFrame(tick);
        } else {
          freezeScrollRafRef.current = null;
        }
      };
      freezeScrollRafRef.current = requestAnimationFrame(tick);
    };

    const lockCalendarScroll = (lock) => {
      const scrollers = Array.from(
        document.querySelectorAll(".fc .fc-scroller, .fc .fc-timegrid-body, .fc .fc-timegrid-body-frame")
      );
      scrollers.forEach((scroller) => {
        if (!(scroller instanceof HTMLElement)) return;
        if (lock) {
          frozenScrollTargetsRef.current.push({
            el: scroller,
            top: scroller.scrollTop,
            left: scroller.scrollLeft,
          });
          if (!scroller.dataset.prevOverflowY) {
            scroller.dataset.prevOverflowY = scroller.style.overflowY || "";
          }
          if (!scroller.dataset.prevOverflowX) {
            scroller.dataset.prevOverflowX = scroller.style.overflowX || "";
          }
          scroller.style.overflowY = "hidden";
          scroller.style.overflowX = "hidden";
        } else {
          scroller.style.overflowY = scroller.dataset.prevOverflowY || "";
          scroller.style.overflowX = scroller.dataset.prevOverflowX || "";
          delete scroller.dataset.prevOverflowY;
          delete scroller.dataset.prevOverflowX;
        }
      });
      if (lock) startFreezeLoop();
      else {
        stopFreezeLoop();
        frozenScrollTargetsRef.current = [];
      }
    };

    const handleMouseDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest(".fc-job")) return;
      isHoldingExternalJobRef.current = true;
      lockCalendarScroll(true);
    };

    const handleMouseUp = () => {
      isHoldingExternalJobRef.current = false;
      lockCalendarScroll(false);
    };

    const handleWindowBlur = () => {
      isHoldingExternalJobRef.current = false;
      lockCalendarScroll(false);
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("mouseup", handleMouseUp, true);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      lockCalendarScroll(false);
      stopFreezeLoop();
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("mouseup", handleMouseUp, true);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  const calendarLegend = (
    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mt-0">
      <div className="flex items-center gap-1.5">
        <span style={{ width: 10, height: 10, borderRadius: "999px", backgroundColor: JOB_STATUS_COLORS["ลงงาน"] || ASSIGNED_EVENT_COLOR, display: "inline-block" }} />
        <span>ลงงาน</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span style={{ width: 10, height: 10, borderRadius: "999px", backgroundColor: JOB_STATUS_COLORS["Check in ทำงาน"], display: "inline-block" }} />
        <span>กำลังทำงาน</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span style={{ width: 10, height: 10, borderRadius: "999px", backgroundColor: JOB_STATUS_COLORS["ปิดงาน รอเซ็น"], display: "inline-block" }} />
        <span>ปิดงาน รอเซ็น</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span style={{ width: 10, height: 10, borderRadius: "999px", backgroundColor: JOB_STATUS_COLORS["ส่งงาน"], display: "inline-block" }} />
        <span>ส่งงาน</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span style={{ width: 10, height: 10, borderRadius: "999px", backgroundColor: JOB_STATUS_COLORS["สำเร็จ"], display: "inline-block" }} />
        <span>สำเร็จ</span>
      </div>
    </div>
  );


  // Fetch teams from API
  const fetchTeams = async () => {
    try {
      const compId = window.localStorage.getItem("company");
      const res = await api.get(`/get-teams?Comp_Id=${compId}`);
      if (res.data?.status === "OK") {
        const teamsData = (res.data.result || [])
          .filter((team) => team.Team_Status === "A") // เฉพาะทีมที่ active
          .map((team) => ({
            id: team.Team_Id,
            name: team.Team_Name,
            color: team.Team_Color || "#3788d8", // ใช้สีจากทีม หรือ default
          }));
        
        setTeams(teamsData);
        
        // ตั้งค่า selectedTeams และ selectedTeamsMonth เป็นทุกทีม (ทั้งหมด) ถ้ายังไม่มี
        if (teamsData.length > 0) {
          if (selectedTeams.length === 0) {
            setSelectedTeams(teamsData.map(t => t.id));
          }
          if (selectedTeamsMonth.length === 0) {
            setSelectedTeamsMonth(teamsData.map(t => t.id));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถโหลดข้อมูลทีมได้",
      });
    }
  };

  // Fetch assigned jobs (events for calendar)
  const fetchAssignedJobs = async (teamId = null) => {
    try {
      const compId = localStorage.getItem("company");
      const startDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      const endDate = moment().add(1, "month").endOf("month").format("YYYY-MM-DD");
      
      console.log("Fetching assigned jobs with params:", { compId, teamId, startDate, endDate });
      
      const response = await api.get("/get-assigned-jobs", {
        params: {
          ...(compId ? { comp_id: compId } : {}),
          ...(teamId ? { team_id: teamId } : {}),
          ...(branchFilter !== "all" ? { branch_id: branchFilter } : {}),
          start_date: startDate,
          end_date: endDate
        }
      });
      
      console.log("get-assigned-jobs API response:", response.data);
      
      if (response.data?.status === "OK" && Array.isArray(response.data.result)) {
        const allJobs = response.data.result.map(job => {
          // ใช้ teamName จาก API โดยตรง หรือ fallback ไปหาใน teams array
          const team = teams.find(t => t.id === job.teamId);
          return {
            ...job,
            teamName: job.teamName || team?.name || `ทีม ${job.teamId}`,
            jobStatus: job.jobStatus || '',
            Round_Id: job.Round_Id,
            Round_No: job.Round_No,
            Round_Date: job.Round_Date
          };
        });
        console.log("fetchAssignedJobs - allJobs count:", allJobs.length);
        console.log("fetchAssignedJobs - first job sample:", allJobs[0]);
        setAllEvents(allJobs);
      } else {
        console.warn("No assigned jobs or invalid response:", response.data);
        setAllEvents([]);
      }
    } catch (error) {
      console.error("Error fetching assigned jobs:", error);
      console.error("Error details:", error.response?.data);
      setAllEvents([]);
      setEvents([]);
    }
  };

  // Fetch pending jobs (for sidebar)
  const fetchPendingJobs = async () => {
    try {
      const compId = localStorage.getItem("company");
      const response = await api.get("/get-pending-jobs", {
        params: {
          ...(compId ? { comp_id: compId } : {}),
          ...(branchFilter !== "all" ? { branch_id: branchFilter } : {}),
        }
      });
      
      if (response.data?.status === "OK" && Array.isArray(response.data.result)) {
        const jobs = response.data.result.map(job => ({
          id: String(job.Round_Id || job.id || job.JobId),
          JobId: job.JobId,
          Round_Id: job.Round_Id,
          Round_No: job.Round_No,
          Round_Date: job.Round_Date,
          title: job.title || `${job.CustomerName || ""} – ${job.ServiceName || ""} (ครั้งที่ ${job.Round_No || ""})`.trim()
        }));
        setRightJobs(jobs);
      } else {
        setRightJobs([]);
      }
    } catch (error) {
      console.error("Error fetching pending jobs:", error);
      setRightJobs([]);
    }
  };

  // Fetch current jobs (for sidebar)
  const fetchCurrentJobs = async () => {
    try {
      const compId = localStorage.getItem("company");
      const response = await api.get("/get-current-jobs", {
        params: {
          ...(compId ? { comp_id: compId } : {}),
          ...(branchFilter !== "all" ? { branch_id: branchFilter } : {}),
        }
      });
      
      if (response.data?.status === "OK" && Array.isArray(response.data.result)) {
        const jobs = response.data.result.map(job => ({
          id: String(job.Round_Id || job.id || job.JobId),
          JobId: job.JobId,
          Round_Id: job.Round_Id,
          Round_No: job.Round_No,
          Round_Date: job.Round_Date,
          title: job.title || `${job.CustomerName || ""} – ${job.ServiceName || ""} (ครั้งที่ ${job.Round_No || ""})`.trim()
        }));
        setCurrentJobsList(jobs);
      } else {
        setCurrentJobsList([]);
      }
    } catch (error) {
      console.error("Error fetching current jobs:", error);
      setCurrentJobsList([]);
    }
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // โหลดข้อมูลทีมก่อน
      await fetchTeams();
      // รอให้ teams โหลดเสร็จก่อน
      await Promise.all([
        fetchPendingJobs(), 
        fetchCurrentJobs(),
        fetchAssignedJobs(),
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // ดึงข้อมูลงานเมื่อ teams เปลี่ยนและมี teams แล้ว
  useEffect(() => {
    if (teams.length > 0) {
      // ดึงข้อมูลทุกทีมเพื่อให้ filter ได้
      fetchAssignedJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, dailyOpen]);

  useEffect(() => {
    if (!teams.length) return;
    fetchPendingJobs();
    fetchCurrentJobs();
    fetchAssignedJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter]);

  // แปลงงานปัจจุบัน (ยังไม่ assign) เป็น event format สำหรับแสดงในปฏิทิน
  const currentJobsAsEvents = useMemo(() => {
    return (currentJobsList || []).map((job) => {
      const roundDate = job.Round_Date ? moment(job.Round_Date).format("YYYY-MM-DD") : null;
      if (!roundDate) return null;
      return {
        id: `current-${job.Round_Id || job.id}`,
        title: (job.title || "").trim() + " (รอ assign)",
        start: roundDate,
        end: roundDate,
        Job_Date: roundDate,
        JobId: job.JobId,
        Round_Id: job.Round_Id,
        Round_No: job.Round_No,
        teamId: null,
        teamName: null,
        jobStatus: "รอ assign",
        isUnassigned: true,
      };
    }).filter(Boolean);
  }, [currentJobsList]);

  // ใช้ key เดียวกันระหว่าง assigned/current เพื่อกัน event ซ้ำเมื่อบางแหล่งไม่มี Round_Id
  const getRoundKey = useCallback((e) => {
    if (!e) return "";
    const jobId = e.JobId ?? e.Job_Id ?? e.id ?? "";
    const rid = e.Round_Id ?? e.roundId ?? null;
    const rno = e.Round_No ?? e.roundNo ?? null;
    const jdate = e.Job_Date || (e.start ? moment(e.start).format("YYYY-MM-DD") : null);
    return `${jobId}-${rid ?? `no:${rno ?? "na"}`}-${jdate ?? "nodate"}`;
  }, []);

  // Filter events ตามทีมที่เลือกและ search query ใน month view (รวมงานที่รอ assign ด้วย)
  useEffect(() => {
    if (!dailyOpen) {
      let filtered = [...allEvents];
      // เพิ่มงานปัจจุบันที่ยังไม่ assign (แสดงรายการวันนี้แม้ยังไม่ได้ assign)
      const assignedIds = new Set(allEvents.map((e) => getRoundKey(e)));
      currentJobsAsEvents.forEach((ce) => {
        const key = getRoundKey(ce);
        if (!assignedIds.has(key)) filtered.push(ce);
      });

      // Filter ตามทีมที่เลือก (งานรอ assign ไม่มีทีม - แสดงเมื่อไม่ filter ทีม)
      if (selectedTeamsMonth.length > 0) {
        filtered = filtered.filter(e => e.teamId != null && selectedTeamsMonth.includes(e.teamId));
      }

      // Filter ตาม search query (ชื่อทีมหรือชื่องาน)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(e => {
          const titleMatch = (e.title || e.Job_Title || "").toLowerCase().includes(query) || false;
          const teamNameMatch = (e.teamName || "").toLowerCase().includes(query) || false;
          return titleMatch || teamNameMatch;
        });
      }

      console.log("Month view filtered events count:", filtered.length);
      setEvents(filtered);
    }
  }, [selectedTeamsMonth, allEvents, currentJobsAsEvents, dailyOpen, searchQuery, getRoundKey]);

  // Filter events สำหรับ daily view ตาม selectedTeams (รวมงานที่รอ assign ด้วย)
  useEffect(() => {
    if (dailyOpen && selectedDate) {
      let filtered = allEvents.filter((e) => {
        const eventDate = e.Job_Date || (e.start ? moment(e.start).format("YYYY-MM-DD") : null);
        if (!eventDate || !moment(eventDate).isSame(selectedDate, "day")) return false;
        if (selectedTeams.length > 0 && !selectedTeams.includes(e.teamId)) return false;
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const titleMatch = (e.title || e.Job_Title || "").toLowerCase().includes(query) || false;
          const teamNameMatch = (e.teamName || "").toLowerCase().includes(query) || false;
          if (!titleMatch && !teamNameMatch) return false;
        }
        return true;
      });
      // เพิ่มงานปัจจุบันที่ตรงวันที่และยังไม่ assign
      const assignedKeys = new Set(filtered.map((e) => getRoundKey(e)));
      currentJobsAsEvents.forEach((ce) => {
        if (moment(ce.Job_Date).isSame(selectedDate, "day") && !assignedKeys.has(getRoundKey(ce))) {
          if (selectedTeams.length === 0) filtered.push(ce);
        }
      });
      console.log("Daily view filtered events count:", filtered.length);
      setEvents(filtered);
    }
  }, [dailyOpen, selectedDate, allEvents, currentJobsAsEvents, selectedTeams, searchQuery, getRoundKey]);

  // Handle cancel job (move back to pending)
  const handleCancelJob = useCallback(async (job) => {
    const target =
      allEvents.find((ev) => String(ev.id) === String(job?.id)) || job || {};
    const status =
      target.jobStatus ||
      target.Job_Status ||
      target.extendedProps?.Job_Status ||
      "";

    if (!isJobCalendarActionAllowed(status)) {
      Swal.fire({
        icon: "warning",
        title: "ไม่สามารถดำเนินการได้",
        text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
      });
      return { cancelled: false, success: false, blocked: true };
    }

    try {
      const response = await api.post(`/cancel-job-calendar/${job.id}`);
      
      if (response.data.status === "OK") {
        // Refresh data
        await Promise.all([
          fetchPendingJobs(),
          fetchCurrentJobs(),
          fetchAssignedJobs()
        ]);
        
        return { cancelled: false, success: true };
      } else {
        throw new Error(response.data.message || "ไม่สามารถยกเลิกงานได้");
      }
    } catch (error) {
      console.error("Error canceling job:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.response?.data?.message || error.message || "ไม่สามารถยกเลิกงานได้",
      });
      return { cancelled: false, success: false, error: true };
    }
  }, [allEvents]);

  // Setup draggable for sidebar jobs and track drag to cancel
  useEffect(() => {
    // สำหรับ month view: ต้องเลือกทีมเดียวเท่านั้นถึงจะลากได้
    const canDrag = dailyOpen || selectedTeamsMonth.length === 1;
    
    // Setup draggable for pending jobs
    const containerPending = document.getElementById("external-events");
    if (containerPending && activeTab === "pending" && rightJobs.length > 0 && canDrag) {
      if (containerPending.draggableInstance) {
        containerPending.draggableInstance.destroy();
      }
      
      const draggable = new Draggable(containerPending, {
        itemSelector: ".fc-job",
        eventData: (el) => {
          return JSON.parse(el.dataset.event);
        }
      });
      
      containerPending.draggableInstance = draggable;
    } else if (containerPending?.draggableInstance) {
      containerPending.draggableInstance.destroy();
      delete containerPending.draggableInstance;
    }

    // Setup draggable for current jobs
    const containerCurrent = document.getElementById("current-jobs-events");
    if (containerCurrent && activeTab === "current" && currentJobsList.length > 0 && canDrag) {
      if (containerCurrent.draggableInstance) {
        containerCurrent.draggableInstance.destroy();
      }
      
      const draggable = new Draggable(containerCurrent, {
        itemSelector: ".fc-job",
        eventData: (el) => {
          return JSON.parse(el.dataset.event);
        }
      });
      
      containerCurrent.draggableInstance = draggable;
    } else if (containerCurrent?.draggableInstance) {
      containerCurrent.draggableInstance.destroy();
      delete containerCurrent.draggableInstance;
    }

    // Track drag from calendar events to sidebar (for cancel job)
    let isDragging = false;
    let draggedEventId = null;

    const handleMouseDown = (e) => {
      const eventEl = e.target.closest('.fc-event');
      const sidebarEl = e.target.closest('#external-events');
      if (eventEl && !sidebarEl) {
        let eventId = null;
        
        eventId = eventEl.getAttribute('data-event-id') || 
                  eventEl.closest('[data-event-id]')?.getAttribute('data-event-id');
        
        if (!eventId) {
          const idMatch = eventEl.id?.match(/fc-event-(.+)/);
          if (idMatch) {
            eventId = idMatch[1];
          }
        }
        
        if (!eventId) {
          const parentEl = eventEl.parentElement?.closest('[data-event-id]');
          eventId = parentEl?.getAttribute('data-event-id');
        }
        
        if (!eventId && calendarRef.current) {
          try {
            const calendarApi = calendarRef.current.getApi();
            const allEvents = calendarApi.getEvents();
            for (const fcEvent of allEvents) {
              const eventEls = document.querySelectorAll(`[data-event-id="${fcEvent.id}"]`);
              if (Array.from(eventEls).includes(eventEl)) {
                eventId = String(fcEvent.id);
                break;
              }
            }
          } catch (err) {
            console.log('Could not get event ID from calendar API:', err);
          }
        }
        
        if (eventId) {
          draggedEventId = String(eventId);
          isDragging = true;
          console.log('Started dragging event:', draggedEventId);
        }
      }
    };

    const handleMouseUp = async (e) => {
      if (!isDragging || !draggedEventId) {
        isDragging = false;
        draggedEventId = null;
        return;
      }

      const externalEventsEl = document.getElementById("external-events");
      if (externalEventsEl && (externalEventsEl.contains(e.target) || externalEventsEl === e.target)) {
        console.log('Dropped on external-events, eventId:', draggedEventId);
        
        const ev = events.find((event) => String(event.id) === String(draggedEventId));
        if (ev) {
          e.preventDefault();
          e.stopPropagation();
          console.log('Dropped on sidebar, canceling job:', draggedEventId, ev.title);

          if (!isJobCalendarActionAllowed(ev.jobStatus)) {
            Swal.fire({
              icon: "warning",
              title: "ไม่สามารถยกเลิกงานได้",
              text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
            });
            isDragging = false;
            draggedEventId = null;
            return;
          }
          
          if (calendarRef.current) {
            try {
              const calendarApi = calendarRef.current.getApi();
              const fcEvent = calendarApi.getEventById(draggedEventId);
              if (fcEvent) {
                fcEvent.remove();
              }
            } catch (err) {
              console.log('Could not remove event from calendar:', err);
            }
          }
          
          handleCancelJob(ev);
        } else {
          console.log('Event not found:', draggedEventId);
        }
      }

      isDragging = false;
      draggedEventId = null;
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      if (containerPending?.draggableInstance) {
        containerPending.draggableInstance.destroy();
        delete containerPending.draggableInstance;
      }
      if (containerCurrent?.draggableInstance) {
        containerCurrent.draggableInstance.destroy();
        delete containerCurrent.draggableInstance;
      }
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [rightJobs, currentJobsList, activeTab, dailyOpen, selectedTeamsMonth, events, handleCancelJob]);

  // Resources for resource view (teams)
  const resources = useMemo(() => {
    return teams.map(team => ({
      id: String(team.id),
      title: team.name,
    }));
  }, [teams]);

  const filteredPendingJobs = useMemo(() => {
    return rightJobs.filter(job => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (job.title?.toLowerCase().includes(query) || false) ||
        (job.teamName?.toLowerCase().includes(query) || false);
    });
  }, [rightJobs, searchQuery]);

  const filteredCurrentJobs = useMemo(() => {
    return currentJobsList.filter(job => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (job.title?.toLowerCase().includes(query) || false) ||
        (job.teamName?.toLowerCase().includes(query) || false);
    });
  }, [currentJobsList, searchQuery]);

  useEffect(() => {
    const totalItems = activeTab === "current" ? filteredCurrentJobs.length : filteredPendingJobs.length;
    const maxPage = Math.max(1, Math.ceil(totalItems / jobListItemsPerPage));
    if (jobListPage > maxPage) {
      setJobListPage(maxPage);
    }
  }, [activeTab, filteredCurrentJobs.length, filteredPendingJobs.length, jobListItemsPerPage, jobListPage]);

  const paginatedPendingJobs = useMemo(() => {
    const start = (jobListPage - 1) * jobListItemsPerPage;
    return filteredPendingJobs.slice(start, start + jobListItemsPerPage);
  }, [filteredPendingJobs, jobListPage, jobListItemsPerPage]);

  const paginatedCurrentJobs = useMemo(() => {
    const start = (jobListPage - 1) * jobListItemsPerPage;
    return filteredCurrentJobs.slice(start, start + jobListItemsPerPage);
  }, [filteredCurrentJobs, jobListPage, jobListItemsPerPage]);

  // Find available time slot for a job
  const findAvailableTimeSlot = (teamId, date, duration = 60, isMonthView = false) => {
    const same = allEvents
      .filter(e => e.teamId === teamId && moment(e.start).isSame(date, "day"))
      .sort((a, b) => moment(a.start) - moment(b.start));

    let start = moment(date).startOf("day");
    if (isMonthView) {
      start = moment(date).hour(9).minute(0).second(0);
      duration = 30;
    }

    if (isMonthView && same.length > 0) {
      const firstEvent = same[0];
      const firstEventStart = moment(firstEvent.start);
      if (firstEventStart.isSameOrAfter(start.clone().add(duration, "minutes"))) {
        return {
          start: start.clone(),
          end: start.clone().add(duration, "minutes"),
        };
      }
      start = moment(firstEvent.end);
      const minutes = start.minute();
      const remainder = minutes % 30;
      if (remainder !== 0) {
        start.add(30 - remainder, "minutes");
      }
    }

    for (let i = 0; i < same.length; i++) {
      const ev = same[i];
      const evStart = moment(ev.start);
      const evEnd = moment(ev.end);
      
      if (start.clone().add(duration, "minutes").isSameOrBefore(evStart)) {
        return {
          start: start.clone(),
          end: start.clone().add(duration, "minutes"),
        };
      }
      
      start = evEnd.clone();
      
      if (isMonthView) {
        const minutes = start.minute();
        const remainder = minutes % 30;
        if (remainder !== 0) {
          start.add(30 - remainder, "minutes");
        }
      }
    }

    return {
      start: start.clone(),
      end: start.clone().add(duration, "minutes"),
    };
  };

  // Handle event receive (drag from sidebar to calendar)
  const handleEventReceive = async (info) => {
    if (!dailyOpen) {
      if (selectedTeamsMonth.length !== 1) {
        info.revert();
        console.warn("Assign skipped: select exactly one team in month view.");
        return;
      }
    }

    const jobData = info.event.extendedProps || {};
    
    let teamId = null;
    
    if (!dailyOpen) {
      teamId = String(selectedTeamsMonth[0]);
    } else {
      if (info.event.resourceId) {
        teamId = String(info.event.resourceId);
      } else if (info.event.getResources) {
        try {
          const resources = info.event.getResources();
          if (resources && resources.length > 0 && resources[0]?.id) {
            teamId = String(resources[0].id);
          }
        } catch (error) {
          console.log("Could not get resources from event:", error);
        }
      } else if (info.resource?.id) {
        teamId = String(info.resource.id);
      }
    }
    
    const dropDate = moment(info.event.start || info.date || moment());
    
    info.event.remove();
    
    const existingJob = jobData.id ? allEvents.find(e => e.id === String(jobData.id)) : null;
    
    const dropTeamId = teamId ? Number(teamId) : (teams.length > 0 ? teams[0].id : null);
    if (!dropTeamId) {
      console.warn("Assign skipped: missing team selection.");
      return;
    }

    let time;
    if (!dailyOpen) {
      time = findAvailableTimeSlot(dropTeamId, dropDate, 60, true);
    } else {
      const startMoment = moment(info.event.start || dropDate);
      const endMoment = info.event.end ? moment(info.event.end) : startMoment.clone().add(1, 'hour');
      time = {
        start: startMoment,
        end: endMoment
      };
    }

    try {
      const compId = localStorage.getItem("company");
      const jobDate = moment(time.start).format("YYYY-MM-DD");
      const timeStart = moment(time.start).format("HH:mm");
      const timeEnd = moment(time.end).format("HH:mm");

      if (existingJob && existingJob.id) {
        await api.delete(`/remove-job-calendar/${existingJob.id}`);
      }

      const response = await api.post("/assign-job-calendar", {
        Contract_Id: jobData.JobId,
        Team_Id: dropTeamId,
        Job_Date: jobDate,
        Time_Start: timeStart,
        Time_End: timeEnd,
        Comp_Id: compId ? parseInt(compId) : null,
        Job_Status: "ลงงาน",
        ...(jobData.Round_No != null ? { Round_No: jobData.Round_No } : {})
      });

      if (response.data?.status === "OK") {
        await Promise.all([
          fetchPendingJobs(),
          fetchCurrentJobs(),
          fetchAssignedJobs()
        ]);
      } else {
        console.error("Assign failed:", response.data?.message);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: response.data?.message || "ไม่สามารถเพิ่มงานลงปฏิทินได้",
        });
      }
    } catch (error) {
      console.error("Error assigning job:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.response?.data?.message || "ไม่สามารถเพิ่มงานลงปฏิทินได้",
      });
    }
  };

  // Handle event drop (move event on calendar)
  const handleEventDrop = async (info) => {
    const eventData = info.event;
    
    let newTeamId = null;
    
    if (info.newResource?.id) {
      newTeamId = String(info.newResource.id);
    } else if (eventData.resourceId) {
      newTeamId = String(eventData.resourceId);
    } else if (eventData.getResources) {
      try {
        const resources = eventData.getResources();
        if (resources && resources.length > 0 && resources[0]?.id) {
          newTeamId = String(resources[0].id);
        }
      } catch (error) {
        console.log("Could not get resources from event:", error);
      }
    }
    
    const existing = allEvents.find((ev) => ev.id === eventData.id);
    if (!existing) {
      console.error("Event not found in allEvents");
      return info.revert();
    }

    if (!isJobCalendarActionAllowed(existing.jobStatus)) {
      Swal.fire({
        icon: "warning",
        title: "ไม่สามารถย้ายงานได้",
        text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
      });
      info.revert();
      return;
    }

    const id = eventData.id;
    const start = info.event.start ? moment(info.event.start) : (eventData.start ? moment(eventData.start) : moment(selectedDate));
    const end = info.event.end ? moment(info.event.end) : (eventData.end ? moment(eventData.end) : start.clone().add(60, "minutes"));
    
    if (!start.isValid() || !end.isValid()) {
      console.error("Invalid start or end time:", { start: info.event.start, end: info.event.end });
      info.revert();
      return;
    }
    
    if (end.isSameOrBefore(start)) {
      end.add(1, 'hour');
    }
    
    if (!newTeamId) {
      newTeamId = existing.teamId;
    } else {
      newTeamId = Number(newTeamId);
    }
    if (!newTeamId) {
      console.error("Could not determine team ID for drop");
      info.revert();
      return;
    }

    try {
      const compId = localStorage.getItem("company");
      const jobDate = moment(start).format("YYYY-MM-DD");
      const timeStart = moment(start).format("HH:mm");
      const timeEnd = moment(end).format("HH:mm");

      await api.delete(`/remove-job-calendar/${id}`);
      
      const response = await api.post("/assign-job-calendar", {
        Contract_Id: existing.JobId || existing.extendedProps?.JobId,
        Team_Id: newTeamId,
        Job_Date: jobDate,
        Time_Start: timeStart,
        Time_End: timeEnd,
        Comp_Id: compId ? parseInt(compId) : null,
        Job_Status: "ลงงาน",
        ...((existing.Round_No ?? existing.extendedProps?.Round_No) != null ? { Round_No: existing.Round_No ?? existing.extendedProps?.Round_No } : {})
      });

      if (response.data?.status === "OK") {
        if (calendarRef.current && dailyOpen) {
          try {
            const calendarApi = calendarRef.current.getApi();
            const event = calendarApi.getEventById(String(id));
            if (event) {
              if (newTeamId && event.getResources) {
                const currentResources = event.getResources();
                const currentResourceId = currentResources && currentResources.length > 0 ? currentResources[0].id : null;
                if (String(currentResourceId) !== String(newTeamId)) {
                  event.setResources([{ id: String(newTeamId) }]);
                }
              }
              event.setDates(start.toDate(), end.toDate());
            }
          } catch (error) {
            console.log("Could not update event in calendar:", error);
          }
        }
        
        const newTeam = teams.find(t => t.id === newTeamId);
        const updatedEvent = {
          ...existing,
          id: String(response.data.Calendar_Id || id),
          start: start.toISOString(),
          end: end.toISOString(),
          teamId: newTeamId,
          teamName: newTeam?.name || `ทีม ${newTeamId}`,
          Job_Date: jobDate,
          Time_Start: timeStart,
          Time_End: timeEnd
        };
        
        setAllEvents((prev) => {
          const filtered = prev.filter(e => e.id !== id);
          return [...filtered, updatedEvent];
        });
        
        fetchAssignedJobs().catch(error => {
          console.error("Error refreshing data:", error);
        });
      } else {
        info.revert();
      }
    } catch (error) {
      console.error("Error updating job assignment:", error);
      info.revert();
    }
  };

  // Handle event resize
  const handleEventResize = async (info) => {
    const existing = allEvents.find((ev) => ev.id === info.event.id);
    if (!existing) {
      return info.revert();
    }

    if (!isJobCalendarActionAllowed(existing.jobStatus)) {
      Swal.fire({
        icon: "warning",
        title: "ไม่สามารถปรับเวลาได้",
        text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
      });
      info.revert();
      return;
    }

    const id = info.event.id;
    const start = info.event.start ? moment(info.event.start) : null;
    let end = info.event.end ? moment(info.event.end) : null;

    if (!start || !start.isValid()) {
      console.error("Invalid start time on resize:", { start: info.event.start });
      info.revert();
      return;
    }
    if (!end || !end.isValid()) {
      end = start.clone().add(1, "hour");
      try {
        info.event.setEnd(end.toDate());
      } catch (e) {
        // ignore
      }
    }
    if (end.isSameOrBefore(start)) {
      end = start.clone().add(5, "minutes");
      try {
        info.event.setEnd(end.toDate());
      } catch (e) {
        // ignore
      }
    }

    try {
      const jobDate = moment(start).format("YYYY-MM-DD");
      const timeStart = moment(start).format("HH:mm");
      const timeEnd = moment(end).format("HH:mm");

      const response = await api.put(`/update-job-calendar/${id}`, {
        Job_Date: jobDate,
        Time_Start: timeStart,
        Time_End: timeEnd
      });

      if (response.data?.status === "OK") {
        if (calendarRef.current && dailyOpen) {
          try {
            const calendarApi = calendarRef.current.getApi();
            const event = calendarApi.getEventById(String(id));
            if (event) {
              event.setStart(start.toDate());
              event.setEnd(end.toDate());
            }
          } catch (error) {
            console.log("Could not update event in calendar:", error);
          }
        }
        
        const patchState = (ev) =>
          ev.id === id
            ? {
                ...ev,
                start: start.toISOString(),
                end: end.toISOString(),
                Job_Date: jobDate,
                Time_Start: timeStart,
                Time_End: timeEnd,
              }
            : ev;

        setAllEvents((prev) => prev.map(patchState));
        setEvents((prev) => prev.map(patchState));
        
        fetchAssignedJobs().catch(error => {
          console.error("Error refreshing data:", error);
        });
      } else {
        info.revert();
      }
    } catch (error) {
      console.error("Error resizing job:", error);
      info.revert();
    }
  };

  // Handle date click - open daily view
  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setDailyOpen(true);
    if (teams.length > 0) {
      setSelectedTeams(teams.map(t => t.id));
    }
    fetchAssignedJobs();
  };

  // เปิด modal แสดงรายละเอียดงาน
  const handleShowJobInfo = async (job, calendarId = null) => {
    try {
      setJobInfoLoading(true);
      setShowJobInfoModal(true);
      setSelectedJobInfo(null);
      setSelectedJobCalendarId(calendarId);

      const jobId = job.JobId || job.id;
      if (!jobId) {
        throw new Error("ไม่พบ JobId");
      }

      const res = await api.get(`/get-contract/${jobId}`);
      if (res.data.status === "OK") {
        const contractData = res.data;
        const roundId = job.Round_Id;
        const roundNo = job.Round_No;
        const roundDate = job.Round_Date;
        
        let selectedRound = null;
        if (roundId && contractData.works) {
          selectedRound = contractData.works.find(w => w.Round_Id === roundId);
        } else if (roundNo && contractData.works) {
          selectedRound = contractData.works.find(w => w.Round_No === roundNo);
        }
        
        let assignInfo = null;
        let finalRoundNo = roundNo;
        let finalRoundId = roundId;
        let finalRoundDate = roundDate;
        let finalSelectedRound = selectedRound;
        
        if (calendarId) {
          const assignedEvent = allEvents.find(e => e.id === String(calendarId));
          if (assignedEvent) {
            assignInfo = {
              Team_Name: assignedEvent.teamName,
              Team_Id: assignedEvent.teamId,
              Job_Date: assignedEvent.start,
              Time_Start: assignedEvent.start ? moment(assignedEvent.start).format("HH:mm") : null,
              Time_End: assignedEvent.end ? moment(assignedEvent.end).format("HH:mm") : null,
              Job_Status: assignedEvent.jobStatus
            };
            
            if (assignedEvent.Round_No) {
              finalRoundNo = assignedEvent.Round_No;
              finalRoundId = assignedEvent.Round_Id;
              finalRoundDate = assignedEvent.Round_Date;
              
              if (finalRoundId && contractData.works) {
                finalSelectedRound = contractData.works.find(w => w.Round_Id === finalRoundId);
              } else if (finalRoundNo && contractData.works) {
                finalSelectedRound = contractData.works.find(w => w.Round_No === finalRoundNo);
              }
            }
            
            if (!finalSelectedRound && assignInfo.Job_Date && contractData.works) {
              const jobDateStr = moment(assignInfo.Job_Date).format("YYYY-MM-DD");
              finalSelectedRound = contractData.works.find(w => 
                w.Round_Date && moment(w.Round_Date).format("YYYY-MM-DD") === jobDateStr
              );
              if (finalSelectedRound) {
                finalRoundNo = finalSelectedRound.Round_No;
                finalRoundId = finalSelectedRound.Round_Id;
                finalRoundDate = finalSelectedRound.Round_Date;
              }
            }
          }
        } else if (roundDate) {
          if (contractData.works) {
            const jobDateStr = moment(roundDate).format("YYYY-MM-DD");
            finalSelectedRound = contractData.works.find(w => 
              w.Round_Date && moment(w.Round_Date).format("YYYY-MM-DD") === jobDateStr
            );
            if (finalSelectedRound) {
              finalRoundNo = finalSelectedRound.Round_No;
              finalRoundId = finalSelectedRound.Round_Id;
              finalRoundDate = finalSelectedRound.Round_Date;
            }
          }
        }
        
        let details = [];
        if (finalSelectedRound && finalSelectedRound.details) {
          details = finalSelectedRound.details;
        } else if (contractData.details) {
          details = contractData.details;
        }
        
        setSelectedJobInfo({
          header: contractData.header || contractData,
          details: details,
          roundNo: finalRoundNo,
          selectedRound: finalSelectedRound,
          assignInfo: assignInfo
        });
      } else {
        throw new Error(res.data.message || "ไม่สามารถโหลดข้อมูลได้");
      }
    } catch (error) {
      console.error("Error loading job info:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.response?.data?.message || error.message || "ไม่สามารถโหลดข้อมูลได้",
      });
      setShowJobInfoModal(false);
    } finally {
      setJobInfoLoading(false);
    }
  };

  // Handle event click
  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();
    
    const target = info.jsEvent.target;
    if (target && (target.classList.contains('fc-event-close-btn') || target.closest('.fc-event-close-btn'))) {
      return;
    }
    
    const eventData = info.event;
    const calendarId = eventData.id;
    const jobData = {
      JobId: eventData.extendedProps?.JobId || eventData.extendedProps?.Job_Id,
      Round_Id: eventData.extendedProps?.Round_Id,
      Round_No: eventData.extendedProps?.Round_No,
      Round_Date: eventData.extendedProps?.Round_Date,
    };
    handleShowJobInfo(jobData, calendarId);
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Month view - Make calendar fill full height */
        .fc-dayGridMonth-view .fc-daygrid-body,
        .fc-dayGridMonth-view .fc-scroller-harness,
        .fc-dayGridMonth-view .fc-scroller {
          height: 100% !important;
        }
        .fc-dayGridMonth-view .fc-scrollgrid {
          height: 100% !important;
        }
        .fc-dayGridMonth-view .fc-scrollgrid-section-body table,
        .fc-dayGridMonth-view .fc-scrollgrid-sync-table {
          height: 100% !important;
          table-layout: fixed !important;
        }
        .fc-dayGridMonth-view .fc-scrollgrid-sync-table tbody {
          height: 100% !important;
        }
        .fc-dayGridMonth-view .fc-scrollgrid-sync-table tr {
          height: calc(100% / 6) !important;
        }
        .fc-dayGridMonth-view .fc-scrollgrid-sync-table td.fc-daygrid-day {
          height: 100% !important;
          padding: 0 !important;
          vertical-align: top !important;
          position: relative !important;
        }
        .fc-dayGridMonth-view .fc-daygrid-day {
          height: 100% !important;
          min-height: 120px !important;
          position: relative !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .fc-dayGridMonth-view .fc-daygrid-day-frame {
          height: 100% !important;
          min-height: 120px !important;
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          padding: 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
        }
        .fc-dayGridMonth-view .fc-daygrid-day-top {
          width: 100% !important;
          padding: 6px 8px 4px 8px !important;
          margin: 0 !important;
          flex-shrink: 0 !important;
          box-sizing: border-box !important;
        }
        .fc-dayGridMonth-view .fc-daygrid-day-number {
          width: 100% !important;
          text-align: center !important;
          font-size: 14px !important;
          font-weight: bold !important;
          padding: 0 !important;
          margin: 0 !important;
          display: block !important;
        }
        .fc {
          font-size: 14px !important;
        }
        .fc-toolbar-title {
          font-size: 18px !important;
        }
        .fc-button {
          font-size: 14px !important;
        }
        .fc-event-title {
          font-size: 14px !important;
        }
        .fc-event {
          font-size: 14px !important;
        }
        .fc-col-header-cell-cushion {
          font-size: 14px !important;
        }
        .fc-timegrid-slot-label {
          font-size: 14px !important;
        }
        .fc-resource-cell {
          font-size: 14px !important;
        }
        .fc-timegrid-body {
          overflow-x: auto !important;
        }
        .fc-scroller-harness {
          overflow-x: auto !important;
        }
        .fc-scroller {
          overflow-x: auto !important;
        }
        .fc-timegrid {
          overflow-x: auto !important;
        }
        .fc-dayGridMonth-view .fc-daygrid-day-events {
          flex: 1 1 auto !important;
          min-height: 0 !important;
          margin: 2px 4px 0 4px !important;
          padding: 0 !important;
          overflow-y: auto !important;
          box-sizing: border-box !important;
        }
        .fc-dayGridMonth-view .fc-daygrid-day,
        .fc-dayGridMonth-view .fc-daygrid-day-frame,
        .fc-dayGridMonth-view .fc-daygrid-day-top,
        .fc-dayGridMonth-view .fc-daygrid-day-events {
          padding-bottom: 0 !important;
          margin-bottom: 0 !important;
        }
        .fc-event {
          position: relative !important;
          cursor: pointer !important;
          pointer-events: auto !important;
        }
        .fc-event-close-btn {
          transition: background-color 0.2s ease, border-color 0.2s ease !important;
          pointer-events: auto !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
        .fc-event-close-btn,
        .fc-event-close-btn * {
          touch-action: none !important;
        }
        .fc-event-title {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .fc-event-title-container {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .fc-daygrid-event {
          overflow: hidden !important;
        }
        .fc-daygrid-event-title {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .fc-timegrid-event {
          overflow: visible !important;
          min-height: 20px !important;
        }
        .fc-timegrid-event-title {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        .fc-timegrid-event .fc-event-main {
          height: 100% !important;
        }
        .fc-timegrid-event .fc-event-main-frame {
          height: 100% !important;
        }
        .fc-timegrid-event-selected,
        .fc-timegrid-event {
          min-height: 20px !important;
          position: absolute !important;
        }
        .fc-timegrid-event-harness {
          position: absolute !important;
        }
        .fc-timegrid-event-harness-abs {
          position: absolute !important;
        }
        /* Status dot in daily view */
        .fc-status-dot {
          pointer-events: none !important;
        }
        /* เว้นที่ด้านซ้ายให้ status dot ใน timegrid (daily view) */
        .fc-timegrid-event .fc-event-main {
          padding-left: 2px !important;
        }
        .fc-timegrid-event .fc-event-time {
          padding-left: 18px !important;
        }
        .fc .fc-scroller,
        .fc .fc-scroller-liquid-absolute {
          overflow: hidden !important;
        }
        .fc .fc-toolbar {
          flex-wrap: nowrap !important;
          align-items: center !important;
        }
        .fc .fc-toolbar-chunk {
          display: inline-flex !important;
          align-items: center !important;
          gap: 8px !important;
          min-width: 0;
        }
        .fc .fc-toolbar-title {
          margin: 0 !important;
          white-space: nowrap !important;
        }
      `}</style>
      <div className="flex gap-6 pl-2 pr-8 pb-0 h-full min-h-0 overflow-hidden">
        {/* Calendar */}
        <div className="flex-1 bg-white p-4 border rounded-xl shadow relative h-full min-h-0 overflow-hidden">
        {dailyOpen && selectedDate ? (
          <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
            <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  top: `${toolbarOverlayTop}px`,
                  transform: "translateY(-50%)",
                  left: `${toolbarLegendLeft}px`,
                  zIndex: 20,
                  background: "transparent",
                  padding: "0",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  minHeight: "36px",
                }}
              >
                {calendarLegend}
              </div>
              <div
                style={{
                  position: "absolute",
                  top: `${toolbarOverlayTop}px`,
                  transform: "translateY(-50%)",
                  right: `${toolbarTeamRight}px`,
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "transparent",
                  padding: "0",
                  borderRadius: "8px",
                  minHeight: "36px",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, display: "flex", alignItems: "center", lineHeight: 1 }}>
                  เลือกทีม:
                </span>
                <TeamMultiSelect
                  teams={teams}
                  selectedTeams={selectedTeams}
                  onChange={setSelectedTeams}
                />
              </div>
              <FullCalendar
                ref={calendarRef}
                plugins={[resourceTimeGridPlugin, interactionPlugin]}
                initialView="resourceTimeGridDay"
                locales={[thLocale]}
                locale="th"
                customButtons={{
                  backToMonth: {
                    text: "กลับเดือน",
                    click: () => setDailyOpen(false),
                  },
                }}
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "backToMonth",
                }}
                resources={resources.filter(r => selectedTeams.includes(Number(r.id)))}
                events={events.map((e) => {
                  const team = teams.find((t) => t.id === e.teamId);
                  let startMoment, endMoment;
                  
                  if (e.start && e.end) {
                    startMoment = moment(e.start);
                    endMoment = moment(e.end);
                  } else if (e.Job_Date && e.Time_Start && e.Time_End) {
                    const timeStart = e.Time_Start.length === 5 ? e.Time_Start : e.Time_Start.substring(0, 5);
                    const timeEnd = e.Time_End.length === 5 ? e.Time_End : e.Time_End.substring(0, 5);
                    startMoment = moment(`${e.Job_Date} ${timeStart}`, "YYYY-MM-DD HH:mm");
                    endMoment = moment(`${e.Job_Date} ${timeEnd}`, "YYYY-MM-DD HH:mm");
                  } else if (e.Job_Date && e.Time_Start) {
                    const timeStart = e.Time_Start.length === 5 ? e.Time_Start : e.Time_Start.substring(0, 5);
                    startMoment = moment(`${e.Job_Date} ${timeStart}`, "YYYY-MM-DD HH:mm");
                    endMoment = startMoment.clone().add(1, 'hour');
                  } else if (e.Job_Date) {
                    startMoment = moment(`${e.Job_Date} 09:00`, "YYYY-MM-DD HH:mm");
                    endMoment = moment(`${e.Job_Date} 10:00`, "YYYY-MM-DD HH:mm");
                  } else {
                    startMoment = moment(selectedDate).hour(9).minute(0);
                    endMoment = moment(selectedDate).hour(10).minute(0);
                  }
                  
                  if (selectedDate && !startMoment.isSame(selectedDate, 'day')) {
                    startMoment = moment(selectedDate).hour(startMoment.hour()).minute(startMoment.minute());
                    endMoment = moment(selectedDate).hour(endMoment.hour()).minute(endMoment.minute());
                  }
                  
                  const startDate = startMoment.toDate();
                  const endDate = endMoment.toDate();

                  // ใช้สีทีมเป็น background, เก็บสีสถานะไว้ใน extendedProps สำหรับ dot
                  const teamColor = team?.color || "#3788d8";
                  const statusColor = getEventColorByStatus(e.jobStatus);

                  return {
                    ...e,
                    id: String(e.id || e.Calendar_Id),
                    title: e.title || e.Job_Title || `Job ${e.id || e.Calendar_Id}`,
                    start: startDate,
                    end: endDate,
                    allDay: false,
                    resourceId: String(e.teamId),
                    backgroundColor: teamColor,
                    borderColor: teamColor,
                    editable: isJobCalendarActionAllowed(e.jobStatus),
                    startEditable: isJobCalendarActionAllowed(e.jobStatus),
                    durationEditable: isJobCalendarActionAllowed(e.jobStatus),
                    extendedProps: {
                      ...e.extendedProps,
                      JobId: e.JobId || e.Job_Id,
                      Round_Id: e.Round_Id,
                      Round_No: e.Round_No,
                      Team_Id: e.teamId,
                      Team_Name: e.teamName || team?.name,
                      statusColor: statusColor, // เก็บสีสถานะไว้สำหรับ dot
                    }
                  };
                })}
                editable={true}
                droppable={true}
                dragScroll={false}
                resourceEditable={true}
                eventStartEditable={true}
                eventDurationEditable={true}
                defaultTimedEventDuration="01:00:00"
                eventReceive={handleEventReceive}
                eventDrop={async (info) => {
                  const externalEventsEl = document.getElementById("external-events");
                  const currentJobsEl = document.getElementById("current-jobs-events");
                  if (info.jsEvent) {
                    const dropX = info.jsEvent.clientX;
                    const dropY = info.jsEvent.clientY;
                    
                    if (externalEventsEl) {
                      const rect = externalEventsEl.getBoundingClientRect();
                      const isOverExternalEvents = 
                        dropX >= rect.left && dropX <= rect.right && 
                        dropY >= rect.top && dropY <= rect.bottom;
                      
                      if (isOverExternalEvents) {
                        const ev = allEvents.find((e) => e.id === info.event.id);
                        if (ev) {
                          info.revert();
                          await handleCancelJob(ev);
                        }
                        return;
                      }
                    }

                    if (currentJobsEl) {
                      const rect = currentJobsEl.getBoundingClientRect();
                      const isOverCurrentJobs = 
                        dropX >= rect.left && dropX <= rect.right && 
                        dropY >= rect.top && dropY <= rect.bottom;
                      
                      if (isOverCurrentJobs) {
                        const ev = allEvents.find((e) => e.id === info.event.id);
                        if (ev) {
                          info.revert();
                          await handleCancelJob(ev);
                        }
                        return;
                      }
                    }
                  }
                  
                  await handleEventDrop(info);
                }}
                eventDidMount={(info) => {
                  const eventEl = info.el;
                  const eventId = info.event.id;
                  
                  if (eventEl.querySelector('.fc-event-close-btn')) {
                    return;
                  }
                  
                  eventEl.style.cursor = 'pointer';
                  eventEl.style.pointerEvents = 'auto';

                  // --- Status dot (มุมซ้ายบน) ---
                  if (!eventEl.querySelector('.fc-status-dot')) {
                    const statusDot = document.createElement('span');
                    statusDot.className = 'fc-status-dot';
                    const dotColor = info.event.extendedProps?.statusColor || ASSIGNED_EVENT_COLOR;
                    statusDot.style.cssText = `
                      position: absolute;
                      top: 5px;
                      left: 5px;
                      width: 10px;
                      height: 10px;
                      border-radius: 50%;
                      background-color: ${dotColor};
                      border: 1.5px solid rgba(255,255,255,0.85);
                      z-index: 10;
                      pointer-events: none;
                      flex-shrink: 0;
                    `;
                    eventEl.appendChild(statusDot);
                  }
                  
                  // --- Close button (X) ---
                  const closeBtn = document.createElement('button');
                  closeBtn.className = 'fc-event-close-btn';
                  closeBtn.innerHTML = '×';
                  closeBtn.style.cssText = `
                    position: absolute;
                    top: 2px;
                    right: 2px;
                    width: 24px;
                    height: 24px;
                    background: rgba(17, 24, 39, 0.72);
                    border: 1px solid rgba(255, 255, 255, 0.7);
                    border-radius: 50%;
                    cursor: pointer;
                    font-size: 18px;
                    line-height: 1;
                    padding: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 100;
                    color: #ffffff;
                    font-weight: bold;
                    pointer-events: auto;
                    transition: background-color 0.2s ease, border-color 0.2s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
                  `;
                  
                  closeBtn.onmouseover = () => {
                    closeBtn.style.background = 'rgba(220, 38, 38, 0.9)';
                    closeBtn.style.borderColor = 'rgba(255, 255, 255, 0.95)';
                  };
                  
                  closeBtn.onmouseout = () => {
                    closeBtn.style.background = 'rgba(17, 24, 39, 0.72)';
                    closeBtn.style.borderColor = 'rgba(255, 255, 255, 0.7)';
                  };
                  
                  closeBtn.onmousedown = (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  };
                  
                  closeBtn.onclick = async (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const rawStatus =
                      allEvents.find((ev) => String(ev.id) === String(eventId))?.jobStatus ||
                      info.event.extendedProps?.Job_Status ||
                      "";
                    if (!isJobCalendarActionAllowed(rawStatus)) {
                      Swal.fire({
                        icon: "warning",
                        title: "ไม่สามารถยกเลิกงานได้",
                        text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
                      });
                      return;
                    }
                    
                    const confirmResult = await Swal.fire({
                      title: "ยืนยันการยกเลิกงาน",
                      text: `ต้องการยกเลิกงาน "${info.event.title}" หรือไม่?`,
                      icon: "question",
                      showCancelButton: true,
                      confirmButtonText: "ยืนยัน",
                      cancelButtonText: "ยกเลิก",
                      confirmButtonColor: "#d33",
                      cancelButtonColor: "#3085d6",
                    });
                    if (!confirmResult.isConfirmed) {
                      return;
                    }

                    const eventData = {
                      id: eventId,
                      title: info.event.title,
                      extendedProps: info.event.extendedProps || {},
                    };
                    
                    await handleCancelJob(eventData);
                  };
                  
                  if (window.getComputedStyle(eventEl).position === 'static') {
                    eventEl.style.position = 'relative';
                  }
                  
                  eventEl.appendChild(closeBtn);
                }}
                eventResize={handleEventResize}
                eventClick={handleEventClick}
                initialDate={selectedDate}
                slotMinTime="08:00:00"
                slotMaxTime="21:00:00"
                height={calendarHeight}
                contentHeight={calendarHeight}
                expandRows={true}
                scrollTime="00:00:00"
                scrollTimeReset={false}
                allDaySlot={false}
                slotDuration="00:30:00"
                slotLabelInterval="01:00:00"
                snapDuration="00:05:00"
                eventDragMinDistance={5}
                resourceAreaWidth="200px"
              />
            </div>
          </div>
        ) : (
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  top: `${toolbarOverlayTop}px`,
                  transform: "translateY(-50%)",
                  left: `${toolbarLegendLeft}px`,
                  zIndex: 20,
                  background: "rgba(255,255,255,0.92)",
                  padding: "2px 8px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  minHeight: "36px",
                }}
              >
                {calendarLegend}
              </div>
              <div
                style={{
                  position: "absolute",
                  top: `${toolbarOverlayTop}px`,
                  transform: "translateY(-50%)",
                  right: `${toolbarTeamRight}px`,
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "rgba(255,255,255,0.92)",
                  padding: "2px 8px",
                  borderRadius: "8px",
                  minHeight: "36px",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, display: "flex", alignItems: "center", lineHeight: 1 }}>
                  เลือกทีม:
                </span>
                <TeamMultiSelect
                  teams={teams}
                  selectedTeams={selectedTeamsMonth}
                  onChange={setSelectedTeamsMonth}
                />
                {selectedTeamsMonth.length !== 1 && (
                  <span style={{ fontSize: "12px", color: "#E13F57", whiteSpace: "nowrap" }}>
                    ⚠ เลือก 1 ทีม เพื่อลากวางงาน
                  </span>
                )}
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                locales={[thLocale]}
                locale="th"
                displayEventTime={false}
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,resourceTimeGridDay",
                }}
                events={events.map(e => {
                const team = teams.find((t) => t.id === e.teamId);
                const startDate = e.Job_Date || (e.start ? moment(e.start).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"));
                const endDate = e.Job_Date || (e.end ? moment(e.end).format("YYYY-MM-DD") : startDate);
                
                let eventTitle = e.title || e.Job_Title || `Job ${e.id || e.Calendar_Id}`;
                eventTitle = eventTitle.replace(/^\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\s*/g, '');
                eventTitle = eventTitle.replace(/^\d{1,2}:\d{2}\s*/g, '');
                eventTitle = eventTitle.replace(/^\d{1,2}\s*[-\s]*\(?\s*/g, '');
                eventTitle = eventTitle.trim();
                
                const eventColor = getEventColorByStatus(e.jobStatus);
                return {
                  ...e,
                  id: String(e.id || e.Calendar_Id),
                  title: eventTitle,
                  start: startDate,
                  end: endDate,
                  backgroundColor: eventColor,
                  borderColor: eventColor,
                  editable: isJobCalendarActionAllowed(e.jobStatus),
                  startEditable: isJobCalendarActionAllowed(e.jobStatus),
                  durationEditable: isJobCalendarActionAllowed(e.jobStatus),
                  extendedProps: {
                    ...e.extendedProps,
                    JobId: e.JobId || e.Job_Id,
                    Round_Id: e.Round_Id,
                    Round_No: e.Round_No,
                    Team_Id: e.teamId,
                    Team_Name: e.teamName || team?.name,
                  }
                };
              })}
              editable={selectedTeamsMonth.length === 1}
              droppable={selectedTeamsMonth.length === 1}
              dragScroll={false}
              eventReceive={handleEventReceive}
              eventAllow={(dropInfo) => {
                if (!dailyOpen && selectedTeamsMonth.length !== 1) {
                  return false;
                }
                return true;
              }}
              eventDrop={async (info) => {
                const externalEventsEl = document.getElementById("external-events");
                const currentJobsEl = document.getElementById("current-jobs-events");
                if (info.jsEvent) {
                  const dropX = info.jsEvent.clientX;
                  const dropY = info.jsEvent.clientY;
                  
                  if (externalEventsEl) {
                    const rect = externalEventsEl.getBoundingClientRect();
                    const isOverExternalEvents = 
                      dropX >= rect.left && dropX <= rect.right && 
                      dropY >= rect.top && dropY <= rect.bottom;
                    
                    if (isOverExternalEvents) {
                      const ev = allEvents.find((e) => e.id === info.event.id);
                      if (ev) {
                        info.revert();
                        await handleCancelJob(ev);
                      }
                      return;
                    }
                  }

                  if (currentJobsEl) {
                    const rect = currentJobsEl.getBoundingClientRect();
                    const isOverCurrentJobs = 
                      dropX >= rect.left && dropX <= rect.right && 
                      dropY >= rect.top && dropY <= rect.bottom;
                    
                    if (isOverCurrentJobs) {
                      const ev = allEvents.find((e) => e.id === info.event.id);
                      if (ev) {
                        info.revert();
                        await handleCancelJob(ev);
                      }
                      return;
                    }
                  }
                }
                
                await handleEventDrop(info);
              }}
              eventDidMount={(info) => {
                const eventEl = info.el;
                const eventId = info.event.id;
                
                if (eventEl.querySelector('.fc-event-close-btn')) {
                  return;
                }
                
                eventEl.style.cursor = 'pointer';
                eventEl.style.pointerEvents = 'auto';
                
                const closeBtn = document.createElement('button');
                closeBtn.className = 'fc-event-close-btn';
                closeBtn.innerHTML = '×';
                closeBtn.style.cssText = `
                  position: absolute;
                  top: 2px;
                  right: 2px;
                  width: 20px;
                  height: 20px;
                  background: rgba(17, 24, 39, 0.72);
                  border: 1px solid rgba(255, 255, 255, 0.7);
                  border-radius: 50%;
                  cursor: pointer;
                  font-size: 15px;
                  line-height: 1;
                  padding: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  z-index: 10;
                  color: #ffffff;
                  font-weight: bold;
                  pointer-events: auto;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
                  transition: background-color 0.2s ease, border-color 0.2s ease;
                `;
                
                closeBtn.onmouseover = () => {
                  closeBtn.style.background = 'rgba(220, 38, 38, 0.9)';
                  closeBtn.style.borderColor = 'rgba(255, 255, 255, 0.95)';
                };
                
                closeBtn.onmouseout = () => {
                  closeBtn.style.background = 'rgba(17, 24, 39, 0.72)';
                  closeBtn.style.borderColor = 'rgba(255, 255, 255, 0.7)';
                };
                
                closeBtn.onclick = async (e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  const rawStatus =
                    allEvents.find((ev) => String(ev.id) === String(eventId))?.jobStatus ||
                    info.event.extendedProps?.Job_Status ||
                    "";
                  if (!isJobCalendarActionAllowed(rawStatus)) {
                    Swal.fire({
                      icon: "warning",
                      title: "ไม่สามารถยกเลิกงานได้",
                      text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
                    });
                    return;
                  }
                  
                  const confirmResult = await Swal.fire({
                    title: "ยืนยันการยกเลิกงาน",
                    text: `ต้องการยกเลิกงาน "${info.event.title}" หรือไม่?`,
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "ยืนยัน",
                    cancelButtonText: "ยกเลิก",
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#3085d6",
                  });
                  if (!confirmResult.isConfirmed) {
                    return;
                  }

                  const eventData = {
                    id: eventId,
                    title: info.event.title,
                    extendedProps: info.event.extendedProps || {},
                  };
                  
                  await handleCancelJob(eventData);
                };
                
                if (window.getComputedStyle(eventEl).position === 'static') {
                  eventEl.style.position = 'relative';
                }
                
                eventEl.appendChild(closeBtn);
              }}
                eventResize={handleEventResize}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                dayCellContent={(dayInfo) => ({ html: dayInfo.dayNumberText })}
                height={calendarHeight}
                contentHeight={calendarHeight}
                expandRows={true}
              />
              </div>
          </div>
        )}
      </div>

      {/* Sidebar - Pending Jobs and Current Jobs */}
      <div className="w-80 bg-white border rounded-xl shadow p-4 flex flex-col h-full">
        {/* Search Bar */}
        <div className="flex gap-2 mb-4 items-center flex-shrink-0">
          <input
            type="text"
            placeholder="ค้นหา"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <button className="px-4 h-10 bg-blue-400 text-white rounded-md hover:bg-blue-500 flex items-center gap-2">
            <Icon icon="mdi:magnify" />
            <span>ค้นหา</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 flex-shrink-0">
          <button
            onClick={() => setActiveTab("current")}
            className={`flex-1 py-1.5 px-4 text-center font-medium rounded transition-colors whitespace-nowrap ${
              activeTab === "current"
                ? "bg-red-600 text-white"
                : "bg-white text-red-600 border border-red-600"
            }`}
          >
            งานปัจจุบัน
          </button>
          <button
            onClick={() => setActiveTab("pending")}
            className={`flex-1 py-1.5 px-4 text-center font-medium rounded transition-colors whitespace-nowrap ${
              activeTab === "pending"
                ? "bg-red-600 text-white"
                : "bg-white text-red-600 border border-red-600"
            }`}
          >
            งานที่ค้างอยู่
          </button>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="text-center py-4 text-gray-500" style={{ fontSize: '14px' }}>กำลังโหลด...</div>
        ) : (
          <>
            {/* Tab: งานค้าง */}
            {activeTab === "pending" && (
              <div className="flex flex-col flex-1 min-h-0">
                <div id="external-events" className="space-y-3 flex-1 overflow-y-auto min-h-0">
                  {filteredPendingJobs.length === 0 ? (
                    <div className="text-center py-4 text-gray-500" style={{ fontSize: '14px' }}>ไม่มีงานค้าง</div>
                  ) : (
                    paginatedPendingJobs.map((job) => (
                      <div
                        key={job.id}
                        className="fc-job p-3 rounded-lg cursor-move hover:shadow-md transition-shadow bg-gray-100 border border-gray-300"
                        data-event={JSON.stringify(job)}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="font-medium flex-1 whitespace-nowrap overflow-hidden text-ellipsis text-gray-800" style={{ fontSize: '14px' }}>
                            {job.title}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowJobInfo(job);
                            }}
                            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-300 bg-gray-200 text-gray-700 transition"
                            title="ดูรายละเอียด"
                          >
                            <Icon icon="mdi:information" className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-1 text-gray-600" style={{ fontSize: '14px' }}>
                          {job.Round_No && (
                            <span>ครั้งที่ {job.Round_No}</span>
                          )}
                          {job.teamName && (
                            <span className="ml-2">• ทีม: {job.teamName}</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {filteredPendingJobs.length > 0 && (
                  <Pagination
                    totalItems={filteredPendingJobs.length}
                    itemsPerPage={jobListItemsPerPage}
                    currentPage={jobListPage}
                    onPageChange={setJobListPage}
                    onItemsPerPageChange={setJobListItemsPerPage}
                    showCountText={false}
                  />
                )}
              </div>
            )}

            {/* Tab: งานปัจจุบัน */}
            {activeTab === "current" && (
              <div className="flex flex-col flex-1 min-h-0">
                <div id="current-jobs-events" className="space-y-3 flex-1 overflow-y-auto min-h-0">
                  {filteredCurrentJobs.length === 0 ? (
                    <div className="text-center py-4 text-gray-500" style={{ fontSize: '14px' }}>ไม่มีงานปัจจุบัน</div>
                  ) : (
                    paginatedCurrentJobs.map((job) => (
                      <div
                        key={job.id}
                        className="fc-job p-3 rounded-lg cursor-move hover:shadow-md transition-shadow bg-gray-100 border border-gray-300"
                        data-event={JSON.stringify(job)}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="font-medium flex-1 whitespace-nowrap overflow-hidden text-ellipsis text-gray-800" style={{ fontSize: '14px' }}>
                            {job.title}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowJobInfo(job);
                            }}
                            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-300 bg-gray-200 text-gray-700 transition"
                            title="ดูรายละเอียด"
                          >
                            <Icon icon="mdi:information" className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-1 text-gray-600" style={{ fontSize: '14px' }}>
                          {job.Round_No && (
                            <span>ครั้งที่ {job.Round_No}</span>
                          )}
                          {job.teamName && (
                            <span className="ml-2">• ทีม: {job.teamName}</span>
                          )}
                          {job.start && (
                            <span className="ml-2">• {moment(job.start).format("DD/MM")} {moment(job.start).format("HH:mm")} - {moment(job.end).format("HH:mm")}</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {filteredCurrentJobs.length > 0 && (
                  <Pagination
                    totalItems={filteredCurrentJobs.length}
                    itemsPerPage={jobListItemsPerPage}
                    currentPage={jobListPage}
                    onPageChange={setJobListPage}
                    onItemsPerPageChange={setJobListItemsPerPage}
                    showCountText={false}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal แสดงรายละเอียดงาน */}
      {showJobInfoModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center" 
          onClick={() => setShowJobInfoModal(false)}
          style={{ 
            zIndex: 10000,
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col m-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Sticky */}
            <div className="sticky top-0 bg-gradient-to-r from-[#E13F57] to-[#c7364d] text-white px-6 py-4 flex items-center justify-between shadow-lg z-10">
              <h2 className="text-sm font-bold flex items-center gap-2">
                <Icon icon="mdi:information" className="w-5 h-5" />
                รายละเอียดบริการ
              </h2>
              <button
                onClick={() => setShowJobInfoModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors text-sm"
                title="ปิด"
              >
                <Icon icon="mdi:close" className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50" style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#cbd5e0 #f7fafc'
            }}>
              {jobInfoLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
                  <p className="mt-2 text-gray-600 text-sm">กำลังโหลดข้อมูล...</p>
                </div>
              ) : selectedJobInfo ? (
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {/* ข้อมูลสัญญา */}
                    {selectedJobInfo.header && (
                      <>
                        {selectedJobInfo.header.Doc_No && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">เลขที่สัญญา</span>
                            {selectedJobInfo.header.Doc_Id ? (
                              <a
                                href={`#/jobfile/${selectedJobInfo.header.Doc_Id}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(`/jobfile/${selectedJobInfo.header.Doc_Id}`, '_blank');
                                }}
                              >
                                {selectedJobInfo.header.Doc_No}
                              </a>
                            ) : (
                              <span className="text-gray-800 font-medium">{selectedJobInfo.header.Doc_No}</span>
                            )}
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">สถานะ</span>
                          <span className={`inline-block w-fit px-2.5 py-0.5 rounded-full text-sm font-medium whitespace-nowrap ${
                            selectedJobInfo.header.Doc_Status === 4 ? 'bg-green-100 text-green-800' :
                            selectedJobInfo.header.Doc_Status === 3 ? 'bg-yellow-100 text-yellow-800' :
                            selectedJobInfo.header.Doc_Status === 2 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedJobInfo.header.Doc_Status === 4 ? "เซ็นแล้ว" : 
                             selectedJobInfo.header.Doc_Status === 3 ? "รอเซ็น" :
                             selectedJobInfo.header.Doc_Status === 2 ? "รออนุมัติ" : "ร่าง"}
                          </span>
                        </div>
                        {selectedJobInfo.header.Doc_Date && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">วันที่สัญญา</span>
                            <span className="text-gray-800 font-medium">
                              {moment(selectedJobInfo.header.Doc_Date).format("DD/MM/YYYY")}
                            </span>
                          </div>
                        )}
                        {selectedJobInfo.header.Ct_Start_Date && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">วันที่เริ่ม</span>
                            <span className="text-gray-800 font-medium">
                              {moment(selectedJobInfo.header.Ct_Start_Date).format("DD/MM/YYYY")}
                            </span>
                          </div>
                        )}
                        {selectedJobInfo.header.Ct_End_Date && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">วันที่สิ้นสุด</span>
                            <span className="text-gray-800 font-medium">
                              {moment(selectedJobInfo.header.Ct_End_Date).format("DD/MM/YYYY")}
                            </span>
                          </div>
                        )}
                        {selectedJobInfo.header.Ref_DocNo && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ใบเสนอราคา</span>
                            {selectedJobInfo.header.Ref_DocID ? (
                              <a
                                href={`#/quote/edit/${selectedJobInfo.header.Ref_DocID}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(`/quote/edit/${selectedJobInfo.header.Ref_DocID}`, '_blank');
                                }}
                              >
                                {selectedJobInfo.header.Ref_DocNo}
                              </a>
                            ) : (
                              <span className="text-gray-800 font-medium">{selectedJobInfo.header.Ref_DocNo}</span>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* ข้อมูลลูกค้า */}
                    {selectedJobInfo.header && (
                      <>
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ชื่อลูกค้า</span>
                          <span className="text-gray-800 font-medium">
                            {selectedJobInfo.header.AR_Name || ""} {selectedJobInfo.header.AR_LastName || ""}
                          </span>
                        </div>
                        {selectedJobInfo.header.DisplayName && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ชื่อในสัญญา</span>
                            <span className="text-gray-800 font-medium">{selectedJobInfo.header.DisplayName}</span>
                          </div>
                        )}
                        {selectedJobInfo.header.AR_Tel && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">เบอร์โทร</span>
                            <span className="text-gray-800 font-medium">{selectedJobInfo.header.AR_Tel}</span>
                          </div>
                        )}
                        {selectedJobInfo.header.AR_ContactName && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ผู้ติดต่อ</span>
                            <span className="text-gray-800 font-medium">
                              {selectedJobInfo.header.AR_ContactName}
                              {selectedJobInfo.header.AR_ContactPosition ? ` (${selectedJobInfo.header.AR_ContactPosition})` : ""}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* ข้อมูลสถานที่บริการ */}
                    {selectedJobInfo.header && (
                      <>
                        {selectedJobInfo.header.ServiceAddr_Name && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ชื่อสถานที่</span>
                            <span className="text-gray-800 font-medium">{selectedJobInfo.header.ServiceAddr_Name}</span>
                          </div>
                        )}
                        <div className="flex flex-col md:col-span-2">
                          <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ที่อยู่</span>
                          <span className="text-gray-800 leading-relaxed">
                            {[
                              selectedJobInfo.header.AddrNo,
                              selectedJobInfo.header.Village,
                              selectedJobInfo.header.Moo ? `ม.${selectedJobInfo.header.Moo}` : null,
                              selectedJobInfo.header.Soi ? `ซ.${selectedJobInfo.header.Soi}` : null,
                              selectedJobInfo.header.Road ? `ถ.${selectedJobInfo.header.Road}` : null,
                              selectedJobInfo.header.SubDistrictName ? `ต.${selectedJobInfo.header.SubDistrictName}` : null,
                              selectedJobInfo.header.DistrictName ? `อ.${selectedJobInfo.header.DistrictName}` : null,
                              selectedJobInfo.header.ProvinceName ? `จ.${selectedJobInfo.header.ProvinceName}` : null,
                              selectedJobInfo.header.Zipcode
                            ].filter(Boolean).join(" ") || "-"}
                          </span>
                        </div>
                      </>
                    )}

                    {/* ข้อมูลการ Assign (ถ้ามี) */}
                    {selectedJobInfo.assignInfo && (
                      <>
                        {selectedJobInfo.assignInfo.Team_Name && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ทีม</span>
                            <span className="text-gray-800 font-medium">{selectedJobInfo.assignInfo.Team_Name}</span>
                          </div>
                        )}
                        {selectedJobInfo.assignInfo.Job_Date && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">วันที่เข้าบริการ</span>
                            <span className="text-gray-800 font-medium">
                              {moment(selectedJobInfo.assignInfo.Job_Date).format("DD/MM/YYYY")}
                            </span>
                          </div>
                        )}
                        {selectedJobInfo.assignInfo.Time_Start && selectedJobInfo.assignInfo.Time_End && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">เวลา</span>
                            <span className="text-gray-800 font-medium">
                              {selectedJobInfo.assignInfo.Time_Start} - {selectedJobInfo.assignInfo.Time_End}
                            </span>
                          </div>
                        )}
                        {selectedJobInfo.assignInfo.Job_Status && (
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">สถานะ</span>
                            <span className={`inline-block w-fit px-2.5 py-0.5 rounded-full text-sm font-medium whitespace-nowrap ${
                              selectedJobInfo.assignInfo.Job_Status === 'ลงงาน' ? 'bg-slate-100 text-slate-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'Check in ทำงาน' || selectedJobInfo.assignInfo.Job_Status === 'working' || selectedJobInfo.assignInfo.Job_Status === 'On-Site' ? 'bg-blue-100 text-blue-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'ปิดงาน รอเซ็น' || selectedJobInfo.assignInfo.Job_Status === 'ปิดงาน เซ็นแล้ว' || selectedJobInfo.assignInfo.Job_Status === 'ปิดงาน' ? 'bg-amber-100 text-amber-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'รอส่งงาน' ? 'bg-teal-100 text-teal-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'ส่งงาน' || selectedJobInfo.assignInfo.Job_Status === 'ส่งงาน (Checkout)' ? 'bg-purple-100 text-purple-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'สำเร็จ' ? 'bg-green-100 text-green-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'ยกเลิก' ? 'bg-red-100 text-red-800' :
                              selectedJobInfo.assignInfo.Job_Status === 'เกิดเวลา' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {getJobStatusDisplayShort(selectedJobInfo.assignInfo.Job_Status)}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Round No */}
                    {selectedJobInfo.roundNo && (
                      <div className="flex flex-col md:col-span-2">
                        <span className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-1">ครั้งที่</span>
                        <span className="text-gray-800 font-medium">{selectedJobInfo.roundNo}</span>
                      </div>
                    )}
                  </div>

                  {/* รายการบริการ */}
                  {selectedJobInfo.details && selectedJobInfo.details.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="font-bold text-gray-800 mb-4 text-sm flex items-center gap-2">
                        <Icon icon="mdi:format-list-bulleted" className="w-5 h-5 text-[#E13F57]" />
                        รายการบริการ
                      </h3>
                      <div className="space-y-3">
                        {selectedJobInfo.details.map((detail, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 border-l-4 border-[#E13F57] hover:bg-gray-100 transition-colors">
                            <div className="font-semibold text-gray-800 text-sm mb-1">{detail.Item_Name || "-"}</div>
                            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                              {detail.Item_Code && (
                                <span className="flex items-center gap-1">
                                  <Icon icon="mdi:identifier" className="w-4 h-4" />
                                  <span>รหัส: <span className="font-medium">{detail.Item_Code}</span></span>
                                </span>
                              )}
                              {detail.Item_Qty && (
                                <span className="flex items-center gap-1">
                                  <Icon icon="mdi:numeric" className="w-4 h-4" />
                                  <span>จำนวน: <span className="font-medium">{detail.Item_Qty} {detail.Item_Unit || ""}</span></span>
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* หมายเหตุ */}
                  {selectedJobInfo.header?.Service_Remark && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="font-bold text-gray-800 mb-3 text-sm flex items-center gap-2">
                        <Icon icon="mdi:note-text" className="w-5 h-5 text-[#E13F57]" />
                        รายละเอียดการบริการที่ระบุในสัญญา
                      </h3>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {selectedJobInfo.header.Service_Remark}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ปุ่มยกเลิกงานและไปหน้าสรุปผล */}
                  {selectedJobCalendarId && (
                    <div className="mt-6 pt-6 border-t border-gray-200 flex flex-row flex-wrap gap-3">
                      <button
                        disabled={!isJobCalendarActionAllowed(selectedJobInfo?.assignInfo?.Job_Status)}
                        onClick={async () => {
                          if (!isJobCalendarActionAllowed(selectedJobInfo?.assignInfo?.Job_Status)) {
                            await Swal.fire({
                              icon: "warning",
                              title: "ไม่สามารถยกเลิกงานได้",
                              text: "งานสถานะนี้ไม่สามารถย้าย ลบ หรือยกเลิกได้ (อนุญาตเฉพาะสถานะ ลงงาน)",
                            });
                            return;
                          }
                          const result = await Swal.fire({
                            title: "ยืนยันการยกเลิกงาน",
                            text: "ต้องการยกเลิกงานนี้และย้ายกลับไปอยู่ในงานที่ค้างหรือไม่?",
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonColor: "#d33",
                            cancelButtonColor: "#3085d6",
                            confirmButtonText: "ยืนยัน",
                            cancelButtonText: "ยกเลิก",
                            didOpen: (popup) => {
                              const swalContainer = popup.closest('.swal2-container');
                              if (swalContainer) {
                                swalContainer.style.zIndex = '20000';
                                swalContainer.classList.add('swal2-container-high-z');
                              }
                            }
                          });

                          if (result.isConfirmed) {
                            try {
                              const response = await api.post(`/cancel-job-calendar/${selectedJobCalendarId}`);
                              
                              if (response.data.status === "OK") {
                                setShowJobInfoModal(false);
                                await Promise.all([
                                  fetchPendingJobs(), 
                                  fetchCurrentJobs(),
                                  fetchAssignedJobs()
                                ]);
                              } else {
                                throw new Error(response.data.message || "ไม่สามารถยกเลิกงานได้");
                              }
                            } catch (error) {
                              console.error("Error canceling job:", error);
                              Swal.fire({
                                icon: "error",
                                title: "เกิดข้อผิดพลาด",
                                text: error.response?.data?.message || error.message || "ไม่สามารถยกเลิกงานได้",
                                didOpen: (popup) => {
                                  const swalContainer = popup.closest('.swal2-container');
                                  if (swalContainer) {
                                    swalContainer.style.zIndex = '20000';
                                    swalContainer.classList.add('swal2-container-high-z');
                                  }
                                }
                              });
                            }
                          }
                        }}
                        className={`flex-1 text-white text-sm py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-md ${
                          isJobCalendarActionAllowed(selectedJobInfo?.assignInfo?.Job_Status)
                            ? "bg-red-600 hover:bg-red-700 hover:shadow-lg"
                            : "bg-gray-400 cursor-not-allowed opacity-70"
                        }`}
                      >
                        <Icon icon="mdi:cancel" className="w-5 h-5" />
                        ยกเลิกงาน
                      </button>
                      <button
                        onClick={() => {
                          const closeId = selectedJobCalendarId || selectedJobInfo?.header?.Doc_Id;
                          if (closeId) {
                            navigate(`/job/close/${closeId}`);
                          }
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                      >
                        <Icon icon="mdi:file-document-edit" className="w-5 h-5" />
                        ดูสรุปผลการให้บริการ
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 text-sm">
                  ไม่พบข้อมูล
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}